import React from 'react';
import { Eye, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const PostPreviewPane = ({ platforms, platformData, formData, renderMediaPreview }) => {
  return (
    <Card className="border-slate-100">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Preview
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue={formData.platforms[0] || 'facebook'}>
          <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1 mb-3">
            {formData.platforms.map((p) => {
              const platform = platformData.find(pl => pl.id === p);
              return (
                <TabsTrigger 
                  key={p} 
                  value={p} 
                  className="text-xs px-2 py-1.5 data-[state=active]:bg-white"
                >
                  {platform?.icon ? (
                    <platform.icon className="h-4 w-4" style={{ color: platform.color }} />
                  ) : (
                    <span>{platform?.emoji}</span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {formData.platforms.map((p) => {
            const platform = platformData.find(pl => pl.id === p);
            return (
              <TabsContent key={p} value={p}>
                {p === 'facebook' && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="p-3 flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">YB</div>
                      <div>
                        <p className="font-semibold text-sm text-slate-900">Your Brand</p>
                        <p className="text-xs text-slate-500">Just now</p>
                      </div>
                    </div>
                    <div className="px-3 pb-3">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{formData.content || 'Your content will appear here...'}</p>
                      {formData.hashtags.length > 0 && <p className="text-blue-600 text-sm mt-2">{formData.hashtags.join(' ')}</p>}
                    </div>
                    {renderMediaPreview('aspect-video')}
                    {formData.link && (
                      <div className="border-t border-slate-100 p-3 bg-slate-50">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">yourwebsite.com</p>
                        <p className="text-sm font-medium text-slate-900 mt-1">Link Preview Title</p>
                      </div>
                    )}
                    <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-slate-400 text-xs cursor-default select-none" title="Preview only">
                      <span>Like</span><span>Comment</span><span>Share</span>
                    </div>
                  </div>
                )}
                
                {p === 'instagram' && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-[2px]">
                          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xs font-bold">YB</div>
                        </div>
                        <span className="font-semibold text-sm">yourbrand</span>
                      </div>
                    </div>
                    {formData.media_urls.length > 0 ? renderMediaPreview('aspect-square') : (
                      <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400"><Image className="w-12 h-12" /></div>
                    )}
                    <div className="p-3 space-y-2">
                      <p className="text-sm"><span className="font-semibold">yourbrand</span>{' '}{formData.content?.substring(0, 125) || 'Your caption here...'}</p>
                      {formData.hashtags.length > 0 && <p className="text-blue-900 text-sm">{formData.hashtags.slice(0, 5).join(' ')}</p>}
                    </div>
                  </div>
                )}
                
                {p === 'twitter' && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold text-sm">YB</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-sm">Your Brand</span>
                          <span className="text-slate-500 text-sm">@yourbrand</span>
                        </div>
                        <p className="text-sm mt-1 text-slate-800 whitespace-pre-wrap">{formData.content?.substring(0, 280) || 'Your tweet here...'}</p>
                        {formData.hashtags.length > 0 && <p className="text-blue-500 text-sm mt-1">{formData.hashtags.slice(0, 3).join(' ')}</p>}
                        {formData.media_urls.length > 0 && <div className="mt-3 rounded-xl overflow-hidden">{renderMediaPreview('aspect-video')}</div>}
                      </div>
                    </div>
                  </div>
                )}
                
                {p === 'linkedin' && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="p-3 flex items-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold">YB</div>
                      <div>
                        <p className="font-semibold text-sm text-slate-900">Your Brand</p>
                        <p className="text-xs text-slate-500">10,000 followers</p>
                      </div>
                    </div>
                    <div className="px-3 pb-3">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{formData.content || 'Your content will appear here...'}</p>
                      {formData.hashtags.length > 0 && <p className="text-blue-600 text-sm mt-2">{formData.hashtags.join(' ')}</p>}
                    </div>
                    <div className="border-t border-slate-100 px-3 py-2 flex items-center gap-4 text-slate-400 text-xs cursor-default select-none" title="Preview only">
                      <span>Like</span><span>Comment</span><span>Repost</span><span>Send</span>
                    </div>
                  </div>
                )}
                
                {p === 'youtube' && (
                  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="aspect-video bg-slate-900 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center"><span className="text-white text-2xl">&#9654;</span></div>
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-sm line-clamp-2">{formData.content?.substring(0, 100) || 'Video Title Here...'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">YB</div>
                        <span className="text-xs text-slate-500">Your Brand</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {(p === 'tiktok' || p === 'pinterest' || p === 'threads' || p === 'bluesky') && (
                  <div className="bg-slate-50 rounded-lg p-4 text-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: platform?.color || '#6366f1' }}>
                        {platform?.emoji || 'YB'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">@yourbrand</p>
                        <p className="text-xs text-slate-500">Just now</p>
                      </div>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{formData.content?.substring(0, platform?.maxChars || 500) || 'Your content will appear here...'}</p>
                    {formData.hashtags.length > 0 && <p className="text-blue-600 mt-2">{formData.hashtags.slice(0, 5).join(' ')}</p>}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PostPreviewPane;
